import React from 'react';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Divider from 'Components/DesignSystem/Divider/Divider';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ConfigField from './ConfigField';
import {
  ConfigField as ConfigFieldDef,
  ConfigTab as ConfigTabDef,
  ConfigValue,
  EnvironmentContext,
  EnvironmentFormState,
} from './config/adminConfig.types';
import { evaluateCondition, makeLookup, readValue } from './config/configRules';

interface ConfigTabProps {
  tab: ConfigTabDef;
  state: EnvironmentFormState;
  ctx: EnvironmentContext;
  errors: Record<string, string>;
  /** The panel decides what a change does (confirm dialogs, prerequisites, dependencies). */
  onFieldChange: (tab: ConfigTabDef, field: ConfigFieldDef, value: ConfigValue) => void;
  /** Several flat keys at once, used by "Use default value". */
  onFlatChange: (values: Record<string, ConfigValue>) => void;
  onFieldBlur: (tab: ConfigTabDef, field: ConfigFieldDef) => void;
}

export const fieldTestId = (tab: ConfigTabDef, field: ConfigFieldDef): string =>
  field.testId ?? `environment-${tab.id}-${field.key}`;

/** Error key of a field: its dbKey on a section tab, `applications.<tab>.<key>` otherwise. */
export const errorPath = (tab: ConfigTabDef, field: ConfigFieldDef): string =>
  tab.apiSection ? field.dbKey ?? field.key : `applications.${tab.id}.${field.key}`;

/** One tab of the Edit Environment panel, drawn entirely from its admin-config definition. */
const ConfigTab: React.FC<ConfigTabProps> = ({ tab, state, ctx, errors, onFieldChange, onFlatChange, onFieldBlur }) => {
  const lookup = makeLookup(state, tab, ctx.responseValues);
  const tabEnabled = evaluateCondition(tab.enabledWhen, lookup);

  const renderUseDefaultCheckbox = (field: ConfigFieldDef, disabled: boolean) => {
    const toggle = field.useDefaultToggle;
    if (!toggle) return null;
    const leaves = field.type === 'group' ? field.children ?? [] : [field];
    const defaults = Object.fromEntries(
      toggle.keys.map((key) => [key, leaves.find((leaf) => (leaf.dbKey ?? leaf.key) === key)?.default]),
    );
    const checked = toggle.keys.every((key) => state.flat[key] === defaults[key]);
    return (
      <Checkbox
        label={toggle.label}
        checked={checked}
        onChange={(isChecked) => isChecked && onFlatChange(defaults)}
        disabled={disabled}
        data-testid={`${fieldTestId(tab, field)}-use-default`}
      />
    );
  };

  // `sectionEnabled`: a section's `enabledWhen` (e.g. every Resolve section but the first needs
  // `enable_resolve`) only disables its controls. Values are kept and saved as they are, so
  // turning an application off and on again never loses its settings.
  const renderField = (field: ConfigFieldDef, sectionEnabled: boolean) => {
    if (!evaluateCondition(field.visibleWhen, lookup)) return null;
    const testId = fieldTestId(tab, field);
    let disabled = !tabEnabled || !sectionEnabled || !evaluateCondition(field.enabledWhen, lookup);
    if (field.contextRule === 'singleProduction') disabled = disabled || (!ctx.isProduction && ctx.hasProductionEnv);
    const helpText =
      field.helpTextWhen && evaluateCondition(field.helpTextWhen.condition, lookup) ? field.helpTextWhen.text : undefined;

    // A group on a section tab: each child is its own flat setting, drawn side by side.
    if (field.type === 'group' && tab.apiSection) {
      return (
        <div className="tw-space-y-3" key={field.key} data-testid={testId}>
          {field.label && <FieldTitle label={field.label} infoMessage={field.tooltip ? field.tooltipText : undefined} />}
          <div className="tw-flex tw-gap-3 tw-items-start">
            {(field.children ?? []).map((child) => (
              <div className="tw-flex-1" key={child.key}>
                <ConfigField
                  field={child}
                  value={readValue(state, tab, child)}
                  onChange={(value) => onFieldChange(tab, child, value)}
                  onBlur={() => onFieldBlur(tab, child)}
                  disabled={disabled || !evaluateCondition(child.enabledWhen, lookup)}
                  errors={errors}
                  path={errorPath(tab, child)}
                  testId={fieldTestId(tab, child)}
                />
              </div>
            ))}
          </div>
          {renderUseDefaultCheckbox(field, disabled)}
        </div>
      );
    }

    const value = field.responseKey ? ctx.responseValues[field.responseKey] : readValue(state, tab, field);
    return (
      <div className="tw-space-y-3" key={field.key}>
        {field.dividerBefore && <Divider />}
        <ConfigField
          field={field}
          value={value}
          onChange={(next) => onFieldChange(tab, field, next)}
          onBlur={() => onFieldBlur(tab, field)}
          disabled={disabled}
          errors={errors}
          path={errorPath(tab, field)}
          testId={testId}
          helpText={helpText}
        />
        {renderUseDefaultCheckbox(field, disabled)}
      </div>
    );
  };

  const visibleSections = tab.sections.filter((section) => evaluateCondition(section.visibleWhen, lookup));
  return (
    <div className="tw-space-y-6" data-testid={`environment-tab-${tab.id}`}>
      {visibleSections.map((section, index) => (
        <React.Fragment key={section.id}>
          {index > 0 && tab.layout?.sectionDividers && <Divider />}
          <section className="tw-space-y-6" data-testid={`environment-${tab.id}-section-${section.id}`}>
            {/*
              A section heading, not a field label: `title-header` (14px/600) at full ink.
              It was a `FieldTitle`, the same 14px/500 tertiary ink as the field labels
              below it, so "Conversation Features" read as one more setting.
            */}
            {section.label && (
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <Typography
                  variant="title-header"
                  as="h3"
                  className="tw-text-ink"
                  data-testid={`environment-${tab.id}-section-${section.id}-title`}
                >
                  {section.label}
                </Typography>
                {section.required && (
                  <Typography variant="title-header" as="span" className="tw-text-tone-danger-ink" aria-hidden="true">
                    *
                  </Typography>
                )}
                {section.tooltip && section.tooltipText && <InfoTooltip message={section.tooltipText} placement="top" />}
              </span>
            )}
            {section.fields.map((field, fieldIndex) => {
              const rendered = renderField(field, evaluateCondition(section.enabledWhen, lookup));
              if (!rendered) return null;
              return (
                <React.Fragment key={field.key}>
                  {section.rowDividers && fieldIndex > 0 && <Divider />}
                  {rendered}
                </React.Fragment>
              );
            })}
          </section>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ConfigTab;
